import React from 'react';
import {
  TextField, Stack, FormControl, InputLabel, Select, MenuItem,
  Radio, RadioGroup, FormControlLabel, FormLabel, Chip,
  Autocomplete, Box, Typography, InputAdornment
} from '@mui/material';
import { splitEqually, remainingAmount, normalizeEmail } from '../../utils/splitMath';
import { formatMoney, currencySymbol } from '../../utils/money';

const personLabel = (person) => (person.isYou ? 'You' : person.name || person.email);

// "Split with" (group or friends), split method and per-person shares.
// Shared by CreateExpenseDialog and SplitTransactionDialog. With `peopleOnly`
// (editing an expense) the group choice is hidden and people are picked directly.
function SplitParticipantsFields({ formData, onChange, friends, groups, participants, amount, peopleOnly = false }) {
  const update = (changes) => onChange({ ...formData, ...changes });
  const equalShares = splitEqually(amount || 0, participants.map(p => p.email));
  const remaining = remainingAmount({ amount, participants, customSplits: formData.customSplits });

  return (
    <>
      {!peopleOnly && (
        <FormControl component="fieldset">
          <FormLabel>Split With</FormLabel>
          <RadioGroup
            row
            value={formData.splitType}
            onChange={(e) => update({ splitType: e.target.value, groupId: '', selectedFriends: [], customSplits: {} })}
          >
            <FormControlLabel value="group" control={<Radio />} label="Group" />
            <FormControlLabel value="friends" control={<Radio />} label="Select Friends" />
          </RadioGroup>
        </FormControl>
      )}

      {formData.splitType === 'group' && !peopleOnly ? (
        <FormControl fullWidth>
          <InputLabel>Select Group</InputLabel>
          <Select
            value={formData.groupId}
            label="Select Group"
            onChange={(e) => update({ groupId: e.target.value, customSplits: {} })}
          >
            {groups.length === 0 ? (
              <MenuItem value="" disabled>No groups available</MenuItem>
            ) : (
              groups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name} ({group.memberCount} members)
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      ) : (
        <Autocomplete
          multiple
          options={friends}
          getOptionLabel={(option) => option.name || option.email}
          isOptionEqualToValue={(option, value) => normalizeEmail(option.email) === normalizeEmail(value.email)}
          value={formData.selectedFriends}
          onChange={(_, newValue) => update({ selectedFriends: newValue })}
          renderInput={(params) => (
            <TextField {...params} label="Select Friends" placeholder="Add friends..." helperText="You are included automatically" />
          )}
          renderValue={(value, getItemProps) =>
            value.map((option, index) => {
              const { key, ...itemProps } = getItemProps({ index });
              return <Chip key={key} label={option.name || option.email} size="small" {...itemProps} />;
            })
          }
        />
      )}

      {participants.length > 1 && (
        <Box>
          <FormControl component="fieldset" fullWidth>
            <FormLabel>Split Method</FormLabel>
            <RadioGroup
              row
              value={formData.splitMethod}
              onChange={(e) => update({ splitMethod: e.target.value })}
            >
              <FormControlLabel value="equal" control={<Radio />} label="Split Equally" />
              <FormControlLabel value="unequal" control={<Radio />} label="Unequal Amounts" />
            </RadioGroup>
          </FormControl>

          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>
              Participants ({participants.length})
            </Typography>
            {formData.splitMethod === 'equal' ? (
              <Stack spacing={0.5}>
                {participants.map((participant, i) => (
                  <Stack key={participant.email} direction="row" justifyContent="space-between">
                    <Typography variant="body2">{personLabel(participant)}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatMoney(equalShares[i]?.shareAmount || 0)}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            ) : (
              <Stack spacing={1}>
                {participants.map((participant) => (
                  <TextField
                    key={participant.email}
                    label={personLabel(participant)}
                    type="number"
                    size="small"
                    value={formData.customSplits[participant.email] ?? ''}
                    onChange={(e) =>
                      update({
                        customSplits: {
                          ...formData.customSplits,
                          [participant.email]: e.target.value
                        }
                      })
                    }
                    slotProps={{
                      input: {
                        startAdornment: <InputAdornment position="start">{currencySymbol()}</InputAdornment>
                      },
                      htmlInput: { min: 0, step: '0.01' }
                    }}
                  />
                ))}
                <Typography
                  variant="body2"
                  color={Math.abs(remaining) < 0.005 ? 'success.main' : 'error.main'}
                >
                  {Math.abs(remaining) < 0.005
                    ? 'All of the amount is assigned'
                    : remaining > 0
                      ? `${formatMoney(remaining)} left to assign`
                      : `${formatMoney(Math.abs(remaining))} over the total`}
                </Typography>
              </Stack>
            )}
          </Box>
        </Box>
      )}
    </>
  );
}

export default SplitParticipantsFields;
